import { type FC } from 'react';
import { type EditorConfiguration } from 'codemirror';
import { Controlled as ReactCodeMirror } from 'react-codemirror2';
import * as CodeMirror from 'codemirror';
import { useCodeMirrorStrictModeHandlers } from '@editor2/hooks/useCodeMirrorStrictModeHandlers';
import { useFlushSyncCallback } from '@editor2/hooks/useFlushSyncCallback';

import Typo from 'typo-js';

import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/xml/xml';

import 'codemirror/addon/lint/lint';
import 'codemirror/addon/lint/lint.css';
import 'codemirror/addon/lint/html-lint';

import 'codemirror/addon/hint/xml-hint.js';
import 'codemirror/addon/hint/html-hint.js';
import 'codemirror/addon/hint/show-hint.css';
import 'codemirror/addon/hint/show-hint.js';

import 'codemirror/addon/fold/foldcode.js';
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/fold/foldgutter.js';
import 'codemirror/addon/fold/brace-fold.js';

import 'codemirror/addon/display/autorefresh.js';
import 'codemirror/addon/display/placeholder.js';

import 'codemirror/addon/mode/overlay.js';

import * as htmlhint from 'htmlhint';

import ShortcutsInfo from '@editor2/components/ShortcutsInfo';
import { htmlSchema } from './schema';
import { lintRules } from './lintRules';
import { htmlBlock } from './styles';

declare global {
	interface Window {
		htmlhint: any;
		clipboardData: any;
	}
}
window.htmlhint = htmlhint;

interface Props {
	value: string;
	options?: EditorConfiguration;
	showTips?: boolean;
	onChange: (value: string) => void;
}

const dictionary = new Typo('en_US', null, null, {
	dictionaryPath: 'https://s3.amazonaws.com/assets.soomo.org/dictionaries'
});

const CodeMirrorHTML: FC<Props> = (props) => {
	const { value, options, showTips = true, onChange } = props;
	const { wrapperRef, handleEditorDidMount, handleEditorWillUnmount } =
		useCodeMirrorStrictModeHandlers();

	// Wrap onChange with flushSync for synchronous updates
	const syncOnChange = useFlushSyncCallback(onChange);

	const codeMirrorOptions: EditorConfiguration = {
		mode: 'spellcheck',
		lineNumbers: true,
		lineWrapping: true,
		autoRefresh: true,
		foldGutter: true,
		lint: { options: { rules: lintRules } },
		gutters: ['CodeMirror-lint-markers', 'CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
		extraKeys: {
			Tab: false,
			'Shift-Tab': false,
			'Shift-Alt-F': 'indentAuto',
			'Shift-Option-F': 'indentAuto',
			'Ctrl-Space': 'autocomplete',
			'Cmd-Space': 'autocomplete'
		},
		hintOptions: { schemaInfo: htmlSchema },
		// Configure history for better undo/redo preservation
		undoDepth: 300,
		historyEventDelay: 2000
	};

	const spellCheck = () => {
		const rx_word = '!"#$%&()*+,-./:;<=>?@[\\]^_`{|}~ ';
		const overlay = {
			token: function (stream, _state) {
				let ch = stream.peek();
				let word = '';

				if (rx_word.includes(ch)) {
					stream.next();
					return null;
				}

				while ((ch = stream.peek()) != null && !rx_word.includes(ch)) {
					word += ch;
					stream.next();
				}

				if (!dictionary.check(word)) return 'spell-error';

				return null;
			}
		};
		return CodeMirror.overlayMode(
			CodeMirror.getMode({ ...codeMirrorOptions, ...options }, 'text/html'),
			overlay
		);
	};

	return (
		<div className={htmlBlock}>
			{showTips && <ShortcutsInfo />}
			<ReactCodeMirror
				ref={wrapperRef}
				value={value}
				options={{ ...codeMirrorOptions, ...options }}
				onBeforeChange={(editor, data, value) => syncOnChange(value)}
				defineMode={{ name: 'spellcheck', fn: spellCheck }}
				editorDidMount={handleEditorDidMount}
				editorWillUnmount={handleEditorWillUnmount}
			/>
		</div>
	);
};

export default CodeMirrorHTML;
